import React, { useState, useEffect } from 'react'
import messages from './messages'
import { useIntl } from 'react-intl'
import {
    Button,
    Input,
    Modal,
    Icon,
    SelectAsync,
    DataTable
} from '@arnsbogroup/react-voicearchive'
import { RowInput } from 'components/datatable'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import DateTime from "react-datetime"
import classNames from 'classnames'
import debounce from 'debounce-promise'

export default NiceModal.create(({ task, order }) => {
    const { formatMessage } = useIntl()
    const modal = useModal()
    const [loading, setLoading] = useState(false)
    const [host, setHost] = useState(order?.contacts?.coordinator)
    const [meetingStart, setMeetingStart] = useState(new Date(task.deadline * 1000))
    const [meetingEnd, setMeetingEnd] = useState(new Date(task.deadline * 1000))
    const [meetingName, setMeetingName] = useState(order.name)

    const [participants, setParticipants] = useState([])

    const createEmptyParticipant = () => ({
        id: Date.now() + Math.random(),
        name: '',
        email: ''
    })

    const removeParticipant = (participantId) => {
        if (participantId != host.id) {
            setParticipants(participants.filter((participant) => participant.id != participantId))
        }
    }

    useEffect(() => {
        let list = [
            {
                id: task?.supplier?.uid,
                name: task?.supplier?.userid,
                email: task?.supplier?.email
            }
        ]

        Object.values(order.contacts).map((contact) => {
            if (contact && contact.id != host?.id) {
                list.push({
                    id: contact?.id,
                    name: contact?.name,
                    email: contact?.email
                })
            }
        })

        setParticipants(list)
    }, [])

    const onSubmit = () => {
        setLoading(true)

        const localStart = new Date(meetingStart)
        const localEnd = new Date(meetingEnd)

        fetch(`/rest/v2/livesession`,{
            method: 'POST',
            body: JSON.stringify({
                taskId: task.uid,
                meetingName: meetingName,
                startsAt: localStart.toISOString(),
                endsAt: localEnd.toISOString(),
                participants: participants,
                host: host,
            }),
        })
        .then((response) => response.json())
        .then(({success, errors}) => {
            setLoading(false)
            if (success) {
                toastr.success(formatMessage(messages.meetingBooked))
                modal.resolve()
                modal.remove()
            } else {
                toastr.error(errors.join(', '))
            }
        })
        .catch((error) => toastr.error(error))
    }

    const onParticipantUpdate = (participant) => {
        if (participant?.id == host?.id) {
            setHost(participant)
        }
        setParticipants((prev) => prev.map((p) => p.id === participant.id ? participant : p))
    }

    const validate = () => {
        let errors = []
        if (!host) {
            errors.push(formatMessage(messages.hostError))
        }

        if (meetingStart < new Date) {
            errors.push(formatMessage(messages.meetingStartError))
        }

        if (meetingStart >= meetingEnd) {
            errors.push(formatMessage(messages.meetingStartLargerThanMeetingEndError))
        }

        if (participants.length < 1) {
            errors.push(formatMessage(messages.participantsError))
        }

        if (errors.length) {
            toastr.error(errors.join(','), 'Errors')
        } else {
            onSubmit()
        }
    }

    return (
        <Modal
            show={modal.visible}
            onClose={() => modal.remove()}
            onOutsideClick={() => modal.remove()}
            onSubmit={() => (loading || !meetingName.length) ? undefined : validate()}
            title={formatMessage(messages.title)}
            dialogPanelClasses="max-w-screen-xl"
        >
            <div className="space-y-4">
                <Input
                    required
                    label={formatMessage(messages.meetingName)}
                    value={meetingName}
                    onChange={({ target: { value } }) => setMeetingName(value)}
                />

                <div className="mb-15 flex">
                    <div className='w-full'>
                        <label className="text-gray-700 text-sm font-medium">{formatMessage(messages.startsAt)}</label>
                        <DateTime
                            className="w-full"
                            value={meetingStart}
                            inputProps={{className: "h-9 mt-2 text-center rounded-sm w-1/2 border-t-gray-300 border"}}
                            closeOnTab
                            onChange={(value) => setMeetingStart(new Date(value))}
                        />
                    </div>
                    <div className='w-full'>
                        <label className="text-gray-700 text-sm font-medium">{formatMessage(messages.endsAt)}</label>
                        <DateTime
                            className="w-full"
                            value={meetingEnd}
                            inputProps={{className: "h-9 mt-2 text-center rounded-sm w-1/2 border-t-gray-300 border"}}
                            closeOnTab
                            onChange={(value) => setMeetingEnd(new Date(value))}
                        />
                    </div>
                </div>

                <div className="mb-15">
                    <div className='w-1/2'>
                        <SelectAsync
                            required
                            value={ !host ? null : {
                                value: host?.id,
                                label: host?.name,
                            }}
                            onChange={(host) => setHost({
                                id: host?.value,
                                name: host?.label,
                                host
                            })}
                            label={formatMessage(messages.host)}
                            isClearable={false}
                            cacheOptions
                            defaultOptions
                            loadOptions={
                                debounce((query) => fetch(`/rest/v1/coordinator?pageSize=100&search[name]=${query}`)
                                .then((response) => response.json())
                                .then((coordinators) => coordinators.map((coordinator) => (
                                    {
                                        value: coordinator.uid,
                                        label: coordinator.name,
                                        coordinator
                                    }
                                ))), 250)
                            }
                        />
                    </div>
                </div>

                <DataTable
                    className="table-fixed"
                    indicatorsClassName="w-12"
                    indicators={[
                        (participant) => (
                        <span className="cursor-pointer" onClick={() => removeParticipant(participant.id)}>
                            <Icon
                                title={formatMessage(messages.remove)}
                                className={classNames('text-xs', participant.id != host.id ? 'text-negative' : 'text-gray-300')}
                                icon={['fal', 'trash-alt']}
                            />
                        </span>
                        ),
                    ]}
                    head={{
                        name: { label: formatMessage(messages.name) },
                        email: { label: formatMessage(messages.email) },
                    }}
                    body={{
                        name: (participant) => 
                            <RowInput
                                className='truncate'
                                placeholder={formatMessage(messages.name)}
                                value={participant.name}
                                onChange={(value) => onParticipantUpdate({ ...participant, name: value })}
                            />,
                        email: (participant) => 
                            <RowInput
                                className='truncate'
                                placeholder={formatMessage(messages.email)}
                                value={participant.email}
                                onChange={(value) => onParticipantUpdate({ ...participant, email: value })}
                            />
                    }}
                    entries={participants}
                />
                <div className='float-right'>
                    <Button 
                        variant='outline'
                        onClick={() => setParticipants([...participants, createEmptyParticipant()])}
                        disabled={loading}
                    >
                        { formatMessage(messages.addParticipant) }
                    </Button>
                </div>

                <Button onClick={validate} disabled={loading || !meetingName.length }>
                    { loading ? formatMessage(messages.loading) : formatMessage(messages.submit) }
                </Button>
            </div>
        </Modal>
    )
})